import { yupResolver } from '@hookform/resolvers/yup'
import DeleteIcon from '@mui/icons-material/Delete'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { UIAction } from 'app/slices/UI'
import { SocketClient } from 'app/socketIO'
import DeleteDialog from 'components/DeleteDialog'
import RichTextEditor from 'components/RichTextEditor'
import SelectItem from 'components/Select'
import ShowImages from 'components/ShowImages'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import axiosClient from 'utils/axiosClient'
import * as yup from 'yup'

interface FormInput {
  eventId: number
  img: string
  summary: string
  content: string
  related: string
}
export default function FeedbackDetail() {
  const { id } = useParams<{ id: string }>()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [showDelete, setShowDelete] = useState(false)
  const [loading, setLoading] = useState(false)
  const events = useAppSelector((s) => s.Event.list)
  const schema = yup.object({
    eventId: yup.string().required('イベントを選択してください。'),
    img: yup.string().required('画像を設定して下さい。'),
    summary: yup.string().required('まとめを入力してください。'),
    content: yup.string().required('内容を入力してください。'),
  })
  const feedback = useAppSelector((s) =>
    s.Feedback.list.find((f) => f.id === Number(id))
  )
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: feedback,
  })
  const [state, setState] = useState({
    showSelectThumbnail: false,
  })
  const dispatch = useAppDispatch()
  useEffect(() => {
    if (id && !feedback) {
      navigate('/feedback')
    }
    SocketClient.socket?.emit('lock', {
      model: 'news',
      id: Number(id),
    })
    return () => {
      SocketClient.socket?.emit('unlock', {
        model: 'news',
        id: Number(id),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    dispatch(
      UIAction.setBreadcrumbs([
        {
          text: 'ホーム',
          url: '/',
        },
        {
          text: 'ご利用者様の声管理',
          url: '/feedback',
        },
        {
          text: '詳細',
          url: '/feedback/' + id,
        },
      ])
    )
  }, [dispatch, id])
  const onSubmitForm: SubmitHandler<FormInput> = async (data) => {
    try {
      setLoading(true)
      await axiosClient.put('/api/feedback/' + id, data)

      enqueueSnackbar('新規変更が完了しました。', {
        variant: 'success',
      })
      navigate('/feedback')
    } catch (error) {
      enqueueSnackbar('エラーが発生しました。', {
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Grid container spacing={1} mt={2}>
      {state.showSelectThumbnail && (
        <ShowImages
          open={state.showSelectThumbnail}
          handleClose={() => setState({ ...state, showSelectThumbnail: false })}
          handleSelect={(val) => {
            setValue('img', val, {
              shouldValidate: true,
            })
            setState({ ...state, showSelectThumbnail: false })
          }}
        />
      )}
      {showDelete && (
        <DeleteDialog
          open={showDelete}
          handleClose={() => setShowDelete(false)}
          apiEndpoint={'/api/feedback/' + id}
          handleFinal={() => navigate('/feedback')}
        >
          削除してもよろしいでしょうか
        </DeleteDialog>
      )}
      <Grid item xs={12}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ borderBottom: 2 }}
        >
          <Typography variant="h5" component="h2" fontWeight="bold">
            詳細情報
          </Typography>
          <Button
            variant="contained"
            size="small"
            component="label"
            color="secondary"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 4,
              transform: 'translateY(-0.4rem)',
            }}
            onClick={() => setShowDelete(true)}
          >
            <DeleteIcon />
            <Typography variant="caption" ml={0.4} mt={0.4}>
              削除
            </Typography>
          </Button>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Container
          disableGutters
          component="form"
          noValidate
          autoComplete="off"
          sx={{
            margin: '0',
            padding: '0 !important',
            mt: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            position: 'relative',
          }}
          onSubmit={handleSubmit(onSubmitForm)}
        >
          <Box
            border="1px solid"
            borderColor={errors.img ? 'red' : '#ccc'}
            borderRadius="0.2rem"
            width="100%"
            maxWidth="380px"
            p={1}
            my={2}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography>サムネイル</Typography>
              <Button
                onClick={() =>
                  setState({ ...state, showSelectThumbnail: true })
                }
                variant="outlined"
                size="small"
              >
                {getValues('img') ? '再選択する' : '選択する'}
              </Button>
            </Box>
            {!!getValues('img') && (
              <Box
                component="img"
                width="100%"
                src={getValues('img')}
                mt={2}
              ></Box>
            )}
          </Box>
          <SelectItem
            items={events}
            value={getValues('eventId')}
            label="イベント名"
            helperText={errors.eventId?.message}
            handleSelect={(val) => {
              setValue('eventId', val, {
                shouldValidate: true,
              })
            }}
          />
          <TextField
            required
            label="概要"
            margin="dense"
            fullWidth
            size="small"
            variant="outlined"
            {...register('summary')}
            error={'summary' in errors}
            helperText={errors.summary?.message}
          />
          <TextField
            label="関連ページ"
            margin="dense"
            fullWidth
            size="small"
            variant="outlined"
            {...register('related')}
            error={'related' in errors}
            helperText={errors.related?.message}
          />
          <Typography>内容</Typography>
          <RichTextEditor
            value={getValues('content')}
            setValue={(val) => setValue('content', val)}
          />
          <Stack mt={4}>
            <LoadingButton
              loading={loading}
              disabled={Object.keys(errors).length > 0}
              size="small"
              color="secondary"
              variant="contained"
              sx={{
                borderRadius: 2,
                width: 200,
                fontWeight: 600,
                fontSize: '1rem',
              }}
              type="submit"
            >
              変更する
            </LoadingButton>
          </Stack>
        </Container>
      </Grid>
    </Grid>
  )
}
